import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, CalendarPlus, NotebookPen, Pencil, Plus, Trash2 } from "lucide-react";
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { KpiCard } from "@/components/common/Page";
import { EmptyState, ErrorState, LoadingState } from "@/components/common/States";
import { StatusBadge } from "@/components/common/StatusBadge";
import { UserAvatar } from "@/components/common/PhotoUpload";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { StudentFormDialog } from "@/components/students/StudentFormDialog";
import { AssessmentFormDialog } from "@/components/students/AssessmentFormDialog";
import { ClassFormDialog } from "@/components/classes/ClassFormDialog";
import { ClassDetailDialog } from "@/components/classes/ClassDetailDialog";
import { NOTE_FIELDS } from "@/components/classes/NotesDialog";
import { PaymentsTable, PackageCard } from "@/components/finance/PaymentsTable";
import { PaymentFormDialog, PackageFormDialog } from "@/components/finance/FinanceForms";
import { PortalAccessCard } from "@/components/students/PortalAccessCard";
import { MaterialsPanel } from "@/components/students/MaterialsPanel";
import { useFetch } from "@/hooks/useFetch";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { BILLING, brl, fmtDate, fmtShort, todayISO } from "@/lib/format";

const Info = ({ label, value }) => (
  <div><p className="text-xs text-muted-foreground">{label}</p><p className="text-sm font-medium">{value || "—"}</p></div>
);

const ClassList = ({ items, onOpen, empty }) =>
  items.length === 0 ? <p className="py-6 text-center text-sm text-muted-foreground">{empty}</p> : (
    <div className="divide-y rounded-xl border bg-card">
      {items.map((c) => (
        <button key={c.id} onClick={() => onOpen(c.id)} className="flex w-full items-center gap-4 px-4 py-3 text-left hover:bg-muted/40" data-testid={`student-class-${c.id}`}>
          <span className="w-24 font-mono text-sm">{fmtDate(c.date)}</span>
          <span className="w-12 font-mono text-sm text-primary">{c.start_time}</span>
          <span className="flex-1 truncate text-sm">{c.subject}{c.attendance?.notes && <span className="block truncate text-xs text-muted-foreground">{c.attendance.notes}</span>}</span>
          <StatusBadge status={c.status} />
        </button>
      ))}
    </div>
  );

const Gated = ({ feature, children }) => {
  const { hasFeature } = useAuth();
  return hasFeature(feature) ? children : <ErrorState error={{ response: { status: 402, data: { detail: "Recurso disponível a partir do plano Básico." } } }} />;
};

export default function StudentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasFeature } = useAuth();
  const student = useFetch(`/students/${id}`);
  const classes = useFetch(`/classes?student_id=${id}`);
  const notes = useFetch(`/notes?student_id=${id}`);
  const payments = useFetch(`/payments?student_id=${id}`, { skip: !hasFeature("financeiro") });
  const packages = useFetch(`/packages?student_id=${id}`, { skip: !hasFeature("financeiro") });
  const assessments = useFetch(`/assessments?student_id=${id}`, { skip: !hasFeature("desempenho") });
  const [modal, setModal] = useState(null);
  const [detail, setDetail] = useState(null);

  const reloadAll = () => [student, classes, notes, payments, packages, assessments].forEach((f) => f.reload());

  if (student.error) return <ErrorState error={student.error} onRetry={student.reload} />;
  if (!student.data) return <LoadingState rows={4} />;
  const s = student.data;
  const st = s.stats;
  const today = todayISO();
  const all = classes.data || [];
  const upcoming = all.filter((c) => c.date >= today && ["agendada", "confirmada"].includes(c.status));
  const history = all.filter((c) => !upcoming.includes(c)).reverse();
  const attendance = history.filter((c) => ["realizada", "faltou"].includes(c.status));

  const remove = async () => {
    try {
      await api.delete(`/students/${id}`);
      toast.success("Aluno excluído");
      navigate("/alunos");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div className="animate-in fade-in duration-300">
      <Link to="/alunos" className="mb-4 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="back-to-students-link"><ArrowLeft className="h-4 w-4" />Alunos</Link>
      <div className="mb-6 flex flex-col gap-4 rounded-2xl border bg-card p-5 sm:flex-row sm:items-center">
        <UserAvatar path={s.photo_path} name={s.name} size="h-16 w-16" textSize="text-xl" />
        <div className="flex-1">
          <div className="flex items-center gap-3"><h1 className="font-heading text-2xl font-extrabold" data-testid="student-detail-name">{s.name}</h1><StatusBadge status={s.status} /></div>
          <p className="text-sm text-muted-foreground">{(s.subjects || []).join(", ") || "Sem disciplinas"} · {BILLING[s.billing_type]}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => setModal("class")} data-testid="student-new-class-btn"><CalendarPlus className="mr-2 h-4 w-4" />Agendar aula</Button>
          <Button variant="outline" onClick={() => setModal("edit")} data-testid="student-edit-btn"><Pencil className="mr-2 h-4 w-4" />Editar</Button>
          <Button variant="ghost" className="text-destructive" onClick={() => setModal("delete")} data-testid="student-delete-btn"><Trash2 className="h-4 w-4" /></Button>
        </div>
      </div>
      <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <KpiCard label="Aulas realizadas" value={st.realizadas} tone="text-accent" testId="student-kpi-done" />
        <KpiCard label="Faltas" value={st.faltas} tone="text-orange-500" testId="student-kpi-absences" />
        <KpiCard label="Presença" value={st.presenca_pct == null ? "—" : `${st.presenca_pct}%`} testId="student-kpi-attendance" />
        <KpiCard label="Em aberto" value={brl(st.pendente)} tone="text-amber-500" testId="student-kpi-pending" />
      </div>

      <Tabs defaultValue="info">
        <TabsList className="mb-4 h-auto w-full justify-start overflow-x-auto">
          {[["info", "Informações"], ["aulas", "Aulas"], ["presenca", "Presença"], ["pagamentos", "Pagamentos"], ["anotacoes", "Anotações"], ["desempenho", "Desempenho"], ["materiais", "Materiais"]].map(([k, l]) => (
            <TabsTrigger key={k} value={k} data-testid={`student-tab-${k}`}>{l}</TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="info">
          <div className="grid gap-5 rounded-xl border bg-card p-5 sm:grid-cols-3">
            <Info label="Telefone" value={s.phone} /><Info label="E-mail" value={s.email} /><Info label="Data de nascimento" value={s.birth_date && fmtDate(s.birth_date)} />
            <Info label="Responsável" value={s.guardian_name} /><Info label="Telefone do responsável" value={s.guardian_phone} /><Info label="E-mail do responsável" value={s.guardian_email} /><Info label="Endereço" value={s.address} />
            <Info label="Escola" value={s.school} /><Info label="Série/ano" value={s.grade} /><Info label="Data de início" value={fmtDate(s.start_date)} />
            <div className="sm:col-span-3"><Info label="Observações" value={s.notes} /></div>
          </div>
          <div className="mt-4"><PortalAccessCard studentId={id} defaultEmail={s.email} /></div>
        </TabsContent>
        <TabsContent value="aulas" className="space-y-6">
          <div><h3 className="mb-2 font-heading font-semibold">Próximas aulas</h3><ClassList items={upcoming} onOpen={setDetail} empty="Nenhuma aula agendada." /></div>
          <div><h3 className="mb-2 font-heading font-semibold">Histórico de aulas</h3><ClassList items={history} onOpen={setDetail} empty="Nenhuma aula no histórico." /></div>
        </TabsContent>
        <TabsContent value="presenca">
          <Gated feature="presenca">
            <p className="mb-3 text-sm text-muted-foreground">{st.realizadas} presenças · {st.faltas} faltas · {st.canceladas} canceladas</p>
            <ClassList items={attendance} onOpen={setDetail} empty="Nenhuma presença registrada." />
          </Gated>
        </TabsContent>
        <TabsContent value="pagamentos" className="space-y-4">
          <Gated feature="financeiro">
            <div className="flex flex-wrap gap-2">
              <Button onClick={() => setModal("payment")} data-testid="student-new-payment-btn"><Plus className="mr-2 h-4 w-4" />Novo pagamento</Button>
              <Button variant="outline" onClick={() => setModal("package")} data-testid="student-new-package-btn">Vender pacote</Button>
            </div>
            {(packages.data || []).length > 0 && <div className="grid gap-3 sm:grid-cols-2">{packages.data.map((p) => <PackageCard key={p.id} pkg={p} />)}</div>}
            {(payments.data || []).length === 0 ? <p className="py-6 text-center text-sm text-muted-foreground">Nenhum pagamento registrado.</p>
              : <PaymentsTable payments={payments.data} onChanged={reloadAll} showStudent={false} />}
          </Gated>
        </TabsContent>
        <TabsContent value="anotacoes">
          {(notes.data || []).length === 0 ? <EmptyState icon={NotebookPen} title="Nenhuma anotação ainda" description="Abra uma aula e registre o conteúdo estudado, dificuldades e tarefas." /> : (
            <ol className="relative space-y-4 border-l pl-6">
              {notes.data.map((n) => (
                <li key={n.id} className="relative" data-testid={`note-item-${n.id}`}>
                  <span className="absolute -left-[31px] top-1.5 h-3 w-3 rounded-full border-2 border-background bg-primary" />
                  <p className="text-xs font-semibold text-muted-foreground">{fmtDate(n.date)} · {n.subject}</p>
                  <div className="mt-1 space-y-1 rounded-xl border bg-card p-4 text-sm">
                    {NOTE_FIELDS.filter(([k]) => n[k]).map(([k, l]) => <p key={k}><span className="font-semibold">{l}: </span>{n[k]}</p>)}
                  </div>
                </li>
              ))}
            </ol>
          )}
        </TabsContent>
        <TabsContent value="desempenho" className="space-y-4">
          <Gated feature="desempenho">
            <Button onClick={() => setModal("assessment")} data-testid="student-new-assessment-btn"><Plus className="mr-2 h-4 w-4" />Nova avaliação</Button>
            {(assessments.data || []).length === 0 ? <p className="py-6 text-center text-sm text-muted-foreground">Nenhuma avaliação registrada.</p> : (
              <>
                <div className="h-56 rounded-xl border bg-card p-4" data-testid="assessment-chart">
                  <ResponsiveContainer>
                    <LineChart data={assessments.data.map((a) => ({ ...a, label: fmtShort(a.date) }))}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                      <XAxis dataKey="label" stroke="hsl(var(--muted-foreground))" fontSize={11} /><YAxis domain={[0, 10]} stroke="hsl(var(--muted-foreground))" fontSize={11} width={30} />
                      <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
                      <Line type="monotone" dataKey="score" name="Nota" stroke="hsl(var(--accent))" strokeWidth={2} dot={{ r: 4 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
                <div className="divide-y rounded-xl border bg-card">
                  {[...assessments.data].reverse().map((a) => (
                    <div key={a.id} className="flex items-center gap-4 px-4 py-3 text-sm" data-testid={`assessment-row-${a.id}`}>
                      <span className="w-24 font-mono">{fmtDate(a.date)}</span>
                      <span className="flex-1">{a.subject}{a.content && ` — ${a.content}`}{a.notes && <span className="block text-xs text-muted-foreground">{a.notes}</span>}</span>
                      <span className="font-mono text-lg font-semibold text-primary">{a.score.toFixed(1)}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </Gated>
        </TabsContent>
        <TabsContent value="materiais">
          <MaterialsPanel studentId={id} />
        </TabsContent>
      </Tabs>

      <StudentFormDialog open={modal === "edit"} onOpenChange={(o) => !o && setModal(null)} student={s} onSaved={reloadAll} />
      <ClassFormDialog open={modal === "class"} onOpenChange={(o) => !o && setModal(null)} defaults={{ student_id: id }} onSaved={reloadAll} />
      <PaymentFormDialog open={modal === "payment"} onOpenChange={(o) => !o && setModal(null)} studentId={id} onSaved={reloadAll} />
      <PackageFormDialog open={modal === "package"} onOpenChange={(o) => !o && setModal(null)} studentId={id} onSaved={reloadAll} />
      <AssessmentFormDialog open={modal === "assessment"} onOpenChange={(o) => !o && setModal(null)} studentId={id} defaultSubject={s.subjects?.[0]} onSaved={reloadAll} />
      <ClassDetailDialog open={!!detail} lessonId={detail} onOpenChange={(o) => !o && setDetail(null)} onChanged={reloadAll} />
      <ConfirmDialog open={modal === "delete"} onOpenChange={(o) => !o && setModal(null)} title={`Excluir ${s.name}?`} destructive confirmLabel="Excluir aluno"
        description="Todas as aulas, pagamentos, anotações e avaliações deste aluno serão removidos permanentemente." onConfirm={remove} />
    </div>
  );
}
